import React, { useEffect, useMemo, useState } from 'react';
import { BookmarkPlus, Bookmark, Loader2, X, Users, Lock } from 'lucide-react';
import { filterTemplatesApi } from '../api';
import { useAuth } from '../context/AuthContext';

/**
 * Reusable saved-filter toolbar. Owner (parent page) supplies:
 *  - scope: unique key per page ('requisitions', 'reports', ...)
 *  - filters: the CURRENT filter object to save
 *  - onApply(filters): callback invoked when the user picks a saved template
 *  - theme: for accents
 *
 * The toolbar renders a "Save filter" button + optional Load dropdown + chip row.
 * SA users see a share toggle icon on each template chip so they can promote
 * a private template to "team" — everyone in that scope then sees it.
 */
export default function SavedFiltersToolbar({ scope, filters, onApply, theme }) {
  const { user } = useAuth();
  const isSuper = user?.role === 'SuperAdmin';
  const myEmail = (user?.email || '').toLowerCase();

  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sharingId, setSharingId] = useState(null);

  const load = async () => {
    try { setTemplates(await filterTemplatesApi.list(scope)); }
    catch { setTemplates([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [scope]);

  const currentJson = useMemo(() => JSON.stringify(filters || {}), [filters]);

  const save = async () => {
    const name = window.prompt(`Name this saved ${scope} filter`);
    if (!name?.trim()) return;
    setSaving(true);
    try {
      await filterTemplatesApi.save({ name: name.trim(), scope, filters: JSON.parse(currentJson) });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete saved filter "${name}"?`)) return;
    try { await filterTemplatesApi.remove(id); await load(); }
    catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  const toggleShare = async (t) => {
    if (!isSuper) return;
    setSharingId(t.id);
    try {
      await filterTemplatesApi.toggleShare(t.id, !t.shared);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Share toggle failed');
    } finally { setSharingId(null); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`saved-filters-${scope}`}>
      {templates.length > 0 && (
        <select
          data-testid={`saved-filters-${scope}-load`}
          value=""
          onChange={e => {
            const t = templates.find(x => x.id === e.target.value);
            if (t) onApply?.(t.filters || {});
          }}
          className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white min-w-[180px]"
        >
          <option value="">Load saved filter…</option>
          {templates.map(t => (
            <option key={t.id} value={t.id}>
              {t.name}{t.shared ? ' · team' : ''}
            </option>
          ))}
        </select>
      )}
      <button
        data-testid={`saved-filters-${scope}-save-btn`}
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#E7E2D3] rounded-md text-[12.5px] bg-white hover:bg-[#F7F4EA] disabled:opacity-50"
        title="Save the current filter set for quick reuse"
      >
        {saving ? <Loader2 size={12} className="animate-spin" /> : <BookmarkPlus size={12} />}
        Save filter
      </button>
      {templates.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-[11.5px] text-[#7a6f5c] ml-1" data-testid={`saved-filters-${scope}-chips`}>
          <Bookmark size={11} />
          {templates.map(t => {
            const isMine = (t.ownerEmail || '').toLowerCase() === myEmail;
            return (
              <span
                key={t.id}
                className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full border ${t.shared ? 'border-[#C6D8C7] bg-[#EEF7EE]' : 'border-[#EFEADC] bg-white'}`}
                data-testid={`saved-filters-${scope}-chip-${t.id}`}
              >
                <button onClick={() => onApply?.(t.filters || {})} className="hover:underline flex items-center gap-1" title={`Apply — owner ${t.ownerName || t.ownerEmail || 'unknown'}`}>
                  {t.shared && <Users size={9} className="text-[#4D7C5F]" />}
                  {t.name}
                </button>
                {isSuper && (
                  <button
                    onClick={() => toggleShare(t)}
                    disabled={sharingId === t.id}
                    className={`ml-0.5 rounded-full px-1 py-0.5 ${t.shared ? 'text-[#4D7C5F] hover:bg-[#DFEEE0]' : 'text-[#7a6f5c] hover:bg-[#F1EBDD]'}`}
                    data-testid={`saved-filters-${scope}-share-${t.id}`}
                    title={t.shared ? 'Team-shared — click to make private' : 'Private — click to share with team'}
                  >
                    {sharingId === t.id ? <Loader2 size={9} className="animate-spin" /> :
                      (t.shared ? <Users size={9} /> : <Lock size={9} />)}
                  </button>
                )}
                {isMine && (
                  <button
                    onClick={() => remove(t.id, t.name)}
                    className="ml-1 hover:text-[#B84A4A]"
                    title="Delete this saved filter"
                    data-testid={`saved-filters-${scope}-remove-${t.id}`}
                  >
                    <X size={10} />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
